import React, {useState, useEffect, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {navigationIcArrowBack24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {IconoirArrowLeft} from '@ey-xd/motif-icon';
import {
	aresTaskViewRoutes,
	clientSideEventNames,
	documentTypes,
	formNavigationButtonType,
	IndividualFitDocumentTypes,
	taskOverviewParameters,
	TemporaryDocumentTypes
} from '../../../../../../util/uiconstants';
import {getFilteredQueryParameter, isNullOrUndefined, labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';
import {
	getSections,
	getTaskIdsWithFitDocuments,
	getStoryBook,
	getTaskDocuments,
	getTasksList
} from '../../../../../../selectors/Ares/selectors';
import {anyBodyHasUnsavedChanges} from '../../../../../../selectors/Forms/formBodySelectors';
import Button from '../../../../../Common/BaseComponentWrappers/MotifButton';
import EllipsesControl from '../../../../../Common/EllipsesControl/EllipsesControl';
import Icon from '../../../../../Common/BaseComponentWrappers/MotifIcon';
import {useQuery} from '../../../../../../util/customHooks';
import {useIsTrackChangesView, useSmartEvidenceSourceId} from '../../../../../../util/Ares/customHooks';
import _ from 'lodash';

/* Form navigation button for FIT V2 documents */
export default function FormNavigationButtonV2({
	type,
	engagementId,
	taskId,
	documentId,
	documentTypeId,
	headerId,
	sectionId,
	variant,
	styleName,
	setSectionAreLoading,
	isDrawerMode,
	isCompareResponsesView = false,
	setBodyDataLoaded,
	...props
}) {
	const navigate = useNavigate();

	const queryParameters = useQuery();

	const isTrackChangesView = useIsTrackChangesView();
	const smartSourceId = useSmartEvidenceSourceId(documentId);
	const aresSortedTaskIdList = useSelector((state) =>
		getTaskIdsWithFitDocuments(state)
	);
	const taskDocumentsList = useSelector((state) => getTaskDocuments(state));
	const tasksList = useSelector((state) => getTasksList(state));
	const sectionsList = useSelector((state) => getSections(state));
	const storyBook = useSelector((state) => getStoryBook(state, documentId));
	const hasChanges = useSelector((state) => anyBodyHasUnsavedChanges(state));
	const [buttonData, setButtonData] = useState({});
	const aresTaskList = isTrackChangesView ? [taskId] : aresSortedTaskIdList;

	const isProfileOrIndependenceForm = Utility.isDocumentIndependenceOrProfileForm(documentTypeId);

	useEffect(() => {
		//This will set the back and next directions for the form navigation button.
		if (aresTaskList && storyBook && storyBook.allIds?.length > 0) {
			if (type === formNavigationButtonType.next) {
				setNext();
			} else {
				setBack();
			}
		}
	}, [storyBook, headerId, sectionId, taskId]);

	//#region Helper functions
	function getStoryBookIndexById(_headerId, _sectionId) {
		const storyBookId = _sectionId ? `${_headerId}-${_sectionId}` : _headerId || 'summary';

		const index = storyBook.allIds.indexOf(storyBookId);
		return index;
	}

	function getStoryBookText(storyBookItem) {
		let storyBookText;

		if (storyBookItem) {
			if (storyBookItem.headerId !== headerId || typeof storyBookItem.sectionId === 'undefined') {
				storyBookText = storyBookItem.headerName;
			} else {
				const section = sectionsList.find((section) => section.id === storyBookItem.sectionId);
				storyBookText = storyBookItem.sectionName + `${(section?.isDuplicate || section?.isCustom) && section?.customLabel ? ` - ${section?.customLabel}` : ''}`;
			}
		}

		return storyBookText;
	}

	function getNextTaskId(_taskId) {
		const currentTaskIndex = aresTaskList.findIndex((id) => id === _taskId);
		let nextTaskId;

		if (currentTaskIndex > -1 && currentTaskIndex < aresTaskList.length - 1) {
			nextTaskId = aresTaskList[currentTaskIndex + 1];
		}

		return nextTaskId;
	}

	function getPreviousTaskId(_taskId) {
		const currentTaskIndex = aresTaskList.findIndex((id) => id === _taskId);
		let previousTaskId;

		if (currentTaskIndex > 0) {
			previousTaskId = aresTaskList[currentTaskIndex - 1];
		}

		return previousTaskId;
	}

	function isSummarySection(sectionId) {
		const section = sectionsList.find((section) => section.id === sectionId);
		return section?.isSummary === true;
	}

	//#endregion

	function setNext() {
		const isSummary = headerId === undefined;
		let nextTaskId;
		let nextText;
		let currentIndex;
		let nextIndex;
		let nextPageData = {};
		let isDisabled = false;
		let canShow = true;

		currentIndex = getStoryBookIndexById(headerId, sectionId);

		// The current page is the last item. Must move to the next task
		if (isSummary || currentIndex === storyBook.allIds.length - 1) {
			nextTaskId = getNextTaskId(taskId);
			if (nextTaskId && isProfileOrIndependenceForm === false) {
				const taskDocumentData = taskDocumentsList?.find((f) => f.tasks?.some((id) => id === nextTaskId));
				const storyBookData = taskDocumentData?.storyBook?.byId[taskDocumentData?.storyBook?.allIds[0]];
				const taskData = tasksList.byId[nextTaskId];
				nextText = taskData?.knowledgeTaskShortName || taskData?.knowledgeTaskTitle || taskData?.taskDisplayName;

				// Next activity's
				nextPageData = {
					documentId: taskDocumentData?.id,
					documentTypeId: taskDocumentData?.documentTypeId,
					headerId: storyBookData?.headerId,
					sectionId: storyBookData?.sectionId
				};
			} else {
				isDisabled = true;
			}
		} else {
			// The current page is not the summary / form's last item (ie last header/section). Move to the next item in the storybook
			nextIndex = getStoryBookIndexById(headerId, sectionId) + 1;
			nextPageData = storyBook.byId[storyBook.allIds[nextIndex]];

			// If the next page is a summary section, skip it for track changes view
			if (!isTrackChangesView || !isSummarySection(nextPageData?.sectionId)) {
				nextTaskId = taskId;
				nextText = getStoryBookText(nextPageData);
			}
		}

		if (nextPageData?.gotoMainActivities && isCompareResponsesView) {
			canShow = false;
		}

		if (isSummarySection(nextPageData?.sectionId) && (isCompareResponsesView || isTrackChangesView)) {
			isDisabled = true;
			nextText = '';
		}

		setButtonData({
			type: formNavigationButtonType.next,
			taskId: nextTaskId,
			text: nextText,
			isDisabled: isDisabled,
			canShow,
			...nextPageData
		});

		if (!nextTaskId && isProfileOrIndependenceForm === false) {
			const isGuidedWorkFlowV2 = Utility.isDocumentTypeFitV2(documentTypeId);

			let showMainActivity =
				!isTrackChangesView &&
				isGuidedWorkFlowV2 &&
				!IndividualFitDocumentTypes.includes(documentTypeId) &&
				!TemporaryDocumentTypes.includes(documentTypeId);

			let queryParams = {..._.omitBy(getFilteredQueryParameter(queryParameters), isNullOrUndefined)};

			const docIds = [documentTypes.ACCOUNT_STANDARD_WORKING_PAPER, documentTypes.ACCOUNT_CONCLUSION];

			if (docIds.includes(documentTypeId)) {
				queryParams = {
					...queryParams,
					filteredTaskCategoryUIds: smartSourceId,
					taskCategoryId: taskOverviewParameters.taskCategoryId.Account
				};
			}

			const prefix = isGuidedWorkFlowV2
				? `/${aresTaskViewRoutes.guidedworkflowV2}`
				: `/${aresTaskViewRoutes.guidedworkflow}`;

			const mainActivitiesUrl = `${prefix}?engagementid=${engagementId}${Utility.appendObjectParams(queryParams)}`;

			if (showMainActivity) {
				setButtonData({
					type: formNavigationButtonType.next,
					text: labels.mainActivities,
					isDisabled: false,
					canShow: true,
					gotoMainActivities: true,
					mainActivitiesUrl
				});
			}
		}
	}

	function setBack() {
		let backIndex;
		let backText;
		let backTaskId;
		let backPageData = {};
		let isDisabled = false;
		let canShow = true;

		const currentIndex = getStoryBookIndexById(headerId, sectionId);
		if (currentIndex === -1) {
			return;
		}

		if (currentIndex === 0) {
			// The current page is the first item (1st header - 1st section || Summary page if the form has no headers)
			// Must get the previous task id and doc id and build the corresponding url
			backTaskId = getPreviousTaskId(taskId);
			if (backTaskId && isProfileOrIndependenceForm === false) {
				const taskDocumentData = taskDocumentsList?.find((f) => f.tasks.some((id) => id === backTaskId));
				const previousTaskLastStoryBookItem = taskDocumentData?.storyBook?.allIds[taskDocumentData?.storyBook?.allIds.length - 1];
				const storyBookData = taskDocumentData?.storyBook?.byId[previousTaskLastStoryBookItem];
				const taskData = tasksList.byId[backTaskId];
				backText = taskData?.knowledgeTaskShortName || taskData?.knowledgeTaskTitle || taskData?.taskDisplayName;

				// Previous activity's
				backPageData = {
					documentId: taskDocumentData?.id,
					documentTypeId: taskDocumentData?.documentTypeId,
					headerId: storyBookData?.headerId,
					sectionId: storyBookData?.sectionId
				};
			} else {
				// First activity + header + section. Disable button
				isDisabled = true;
			}
		} else {
			// There is a previous item in the storybook. Move to the previous item
			backIndex = currentIndex - 1;
			backPageData = storyBook.byId[storyBook.allIds[backIndex]];

			// If the previous page is a summary section, skip it for track changes view
			if (!isTrackChangesView || !isSummarySection(backPageData?.sectionId)) {
				backTaskId = taskId;
				backText = getStoryBookText(backPageData);
			}
		}

		if (backPageData?.gotoMainActivities && isCompareResponsesView) {
			canShow = false;
		}

		setButtonData({
			type: formNavigationButtonType.back,
			taskId: backTaskId,
			text: backText,
			isDisabled: isDisabled,
			canShow,
			...backPageData
		});
	}

	const onClick = useCallback(() => {
		if (buttonData?.gotoMainActivities === true) {
			if (buttonData?.mainActivitiesUrl) {
				navigate(buttonData.mainActivitiesUrl);
			} else {
				navigate(
					Utility.getAresTaskListNavigationUrl(
						undefined,
						undefined,
						undefined,
						engagementId,
						undefined,
						queryParameters,
						documentTypes.GUIDEDWORKFLOWV2
					)
				);
			}
		} else {
			let url = Utility.getAresTaskListNavigationUrl(
				buttonData.documentId,
				buttonData?.headerId,
				buttonData?.sectionId,
				engagementId,
				buttonData?.taskId,
				queryParameters,
				buttonData.documentTypeId
			);

			if (isCompareResponsesView) {
				url = Utility.getAresTaskListNavigationUrl(
					buttonData.documentId,
					buttonData?.headerId,
					buttonData?.sectionId,
					engagementId,
					buttonData?.taskId,
					queryParameters,
					buttonData.documentTypeId,
					false,
					true
				);
			}

			navigate(url);
		}

		// Dispatch these events if there are no unsaved changes in the displayed bodies
		if (!hasChanges) {
			setBodyDataLoaded && setBodyDataLoaded(false);

			setTimeout(() => {
				dispatchEvent(new CustomEvent(clientSideEventNames.closeTypeEightGuidanceModal));
				dispatchEvent(new CustomEvent(clientSideEventNames.closeCommentsModal, {event: {data: 'fromNavigation'}}));
				dispatchEvent(new CustomEvent(clientSideEventNames.aresGoToActiveSlideHeaders));
				dispatchEvent(new CustomEvent(clientSideEventNames.aresGoToActiveSlideTasks));
			}, 200);
		}
		document.getElementById(`${documentId}-${headerId}-${sectionId}-${type}-button`).blur();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isDrawerMode,
		buttonData,
		hasChanges,
		navigate,
		engagementId,
		queryParameters,
		isTrackChangesView,
		isCompareResponsesView
	]);

	return (
		<>
			{buttonData.canShow && (
				<StyledFormNavigationButtonV2 className={`StyledFormNavigationButtonV2 ${styleName}`}>
					<Button
						id={`${documentId}-${headerId}-${sectionId}-${type}-button`}
						type="button"
						size="medium"
						variant={variant}
						onClick={onClick}
						disabled={buttonData.isDisabled}
						children={
							<>
								{type === formNavigationButtonType.back && (
									<Icon className={`BackNavButton${buttonData.isDisabled ? ' disabled' : ''}`} src={IconoirArrowLeft} />
								)}
								{type === formNavigationButtonType.next && (
									<>
										<section className={`NextLabel NextNavButton${buttonData.isDisabled ? ' disabled' : ''}`}>
											{`${labels.next}:`}
										</section>
										<EllipsesControl content={buttonData.text} tooltip={buttonData.text} noOfLines={1} />
									</>
								)}
							</>
						}
					/>
				</StyledFormNavigationButtonV2>
			)}
		</>
	);
}

const StyledFormNavigationButtonV2 = styled.section`
	.BackNavButton,
	.NextNavButton {
		&.disabled {
			opacity: 0.5;
			pointer-events: none;
		}
	}
`;
